import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faBoxOpen, faFileInvoiceDollar, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import LinkProduto from 'components/common/LinkProduto';
import { useNFe, useExcluirItem } from '../../../../hooks/useNFe';
import { ItemNFe } from '../../../../types/nfe';
import ModalItemNFe from './ModalItemNFe';
import ModalTributacaoItemNFe from './ModalTributacaoItemNFe';
import BarraAdicionarItem from './BarraAdicionarItem';

// ── Local self-contained components ──────────────────────────────────────────

const BotaoAdicionarItemAvulso: React.FC<{ nfeId: string; crt: string; empresaId: string }> = ({
  nfeId, crt, empresaId,
}) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-body-secondary text-decoration-none"
        style={{ fontSize: '0.78rem' }}
        onClick={() => setShow(true)}
        title="Adicionar item avulso (sem cadastro)"
      >
        <FontAwesomeIcon icon={faPlus} className="me-1" />
        Item avulso
      </button>
      <ModalItemNFe show={show} onHide={() => setShow(false)} nfeId={nfeId} crt={crt} empresaId={empresaId} />
    </>
  );
};

const BotaoEditarItemNFe: React.FC<{ nfeId: string; item: ItemNFe; crt: string; empresaId: string }> = ({
  nfeId, item, crt, empresaId,
}) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Button variant="subtle-secondary" size="sm" onClick={() => setShow(true)} title="Editar item">
        <FontAwesomeIcon icon={faEdit} />
      </Button>
      <ModalItemNFe show={show} onHide={() => setShow(false)} nfeId={nfeId} item={item} crt={crt} empresaId={empresaId} />
    </>
  );
};

const BotaoTributacaoItemNFe: React.FC<{ nfeId: string; item: ItemNFe }> = ({ nfeId, item }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Button variant="subtle-secondary" size="sm" onClick={() => setShow(true)} title="Editar tributação">
        <FontAwesomeIcon icon={faFileInvoiceDollar} />
      </Button>
      <ModalTributacaoItemNFe show={show} onHide={() => setShow(false)} nfeId={nfeId} item={item} />
    </>
  );
};

const BotaoExcluirItemNFe: React.FC<{ nfeId: string; itemId: string }> = ({ nfeId, itemId }) => {
  const { showToast } = useToast();
  const mutation = useExcluirItem(nfeId);

  const handleExcluir = async () => {
    if (!window.confirm('Deseja realmente excluir este item?')) return;
    try {
      await mutation.mutateAsync(itemId);
      showToast('Item excluído com sucesso', 'success');
    } catch {
      // handled by interceptor
    }
  };

  return (
    <Button variant="subtle-danger" size="sm" onClick={handleExcluir} disabled={mutation.isPending} title="Excluir">
      <FontAwesomeIcon icon={faTrash} />
    </Button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface SecaoItensProps {
  nfeId: string;
  isReadOnly: boolean;
}

const formatarMoeda = (valor?: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor ?? 0);

const formatarNumero = (valor: number, decimais = 2) =>
  new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais,
  }).format(valor);

const SecaoItens: React.FC<SecaoItensProps> = ({ nfeId, isReadOnly }) => {
  const { data: nfe } = useNFe(nfeId);
  const itens = nfe?.itens ?? [];
  const crt = nfe?.emitente?.regimeTributario ?? '3';
  const empresaId = nfe?.empresaId ?? '';
  const totalItens = itens.reduce((acc, item) => acc + (item.valorTotal || 0), 0);

  return (
    <>
      <div className="nfe-dest-section-header">
        <span className="nfe-dest-section-title">
          Itens
          {itens.length > 0 && (
            <span
              className="ms-2 badge bg-secondary-subtle text-secondary-emphasis"
              style={{ fontSize: '0.6rem', fontWeight: 700, verticalAlign: 'middle' }}
            >
              {itens.length}
            </span>
          )}
        </span>
        {!isReadOnly && <BotaoAdicionarItemAvulso nfeId={nfeId} crt={crt} empresaId={empresaId} />}
      </div>

      {!isReadOnly && <BarraAdicionarItem nfeId={nfeId} />}

      {itens.length === 0 ? (
        <div className="nfe-empty-state">
          <FontAwesomeIcon icon={faBoxOpen} className="nfe-empty-state__icon" />
          <span className="nfe-empty-state__title">Nenhum item adicionado</span>
          {!isReadOnly && (
            <span className="nfe-empty-state__hint">Busque um produto acima para começar</span>
          )}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table nfe-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Código</th>
                <th>Descrição</th>
                <th className="text-center">NCM</th>
                <th className="text-center">CFOP</th>
                <th className="text-center">CST/CSOSN</th>
                <th className="text-center">Un.</th>
                <th className="text-end">Qtd.</th>
                <th className="text-end">Vlr. Unit.</th>
                <th className="text-end">Total</th>
                {!isReadOnly && <th style={{ width: 110 }} />}
              </tr>
            </thead>
            <tbody>
              {itens.map(item => (
                <tr key={item.id}>
                  <td className="text-body-tertiary">{item.numeroItem}</td>
                  <td className="text-body-secondary" style={{ whiteSpace: 'nowrap' }}>
                    {item.codigoProduto}
                  </td>
                  <td className="text-body-emphasis fw-medium">
                    {item.produtoId ? (
                      <LinkProduto produtoId={item.produtoId} showIcon={false}>
                        {item.nomeProduto}
                      </LinkProduto>
                    ) : (
                      item.nomeProduto
                    )}
                  </td>
                  <td className="text-center text-body-tertiary">{item.ncm}</td>
                  <td className="text-center text-body-tertiary">{item.cfop}</td>
                  <td className="text-center text-body-tertiary">
                    {item.tributacao.cst?.trim() || item.tributacao.csosn?.trim() || '—'}
                  </td>
                  <td className="text-center text-body-secondary">{item.unidadeComercial}</td>
                  <td className="text-end text-body-secondary">
                    {formatarNumero(item.quantidadeComercial, 4)}
                  </td>
                  <td className="text-end text-body-secondary">
                    {formatarMoeda(item.valorUnitario)}
                  </td>
                  <td className="text-end fw-semibold text-primary-emphasis">
                    {formatarMoeda(item.valorTotal)}
                  </td>
                  {!isReadOnly && (
                    <td>
                      <div className="d-flex justify-content-end gap-1">
                        <BotaoEditarItemNFe nfeId={nfeId} item={item} crt={crt} empresaId={empresaId} />
                        <BotaoTributacaoItemNFe nfeId={nfeId} item={item} />
                        <BotaoExcluirItemNFe nfeId={nfeId} itemId={item.id} />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={isReadOnly ? 9 : 10}
                  className="text-end text-body-secondary"
                  style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.03em' }}
                >
                  TOTAL DOS ITENS
                </td>
                <td className="text-end fw-bold text-primary-emphasis">
                  {formatarMoeda(totalItens)}
                </td>
                {!isReadOnly && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
};

export default SecaoItens;
